import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { SYSTEM_ROLE_LABELS, type SystemRole } from "@/lib/constants";
import type { FolderAccessPerson } from "@/lib/folder-access";

/**
 * «Hvem har tilgang» — med KILDEN synlig per person (VIB-2726).
 *
 * Hele poenget: når tilgang kan komme fra to steder, må noen kunne se svaret
 * uten å regne det ut i hodet. Ellers er modellen trygg i databasen og utrygg i
 * praksis.
 *
 * Lista er en FORKLARING, ikke en håndhevelse — den speiler
 * `can_read_document_folder`, men det er SQL-en som avgjør. Derfor sier teksten
 * under lista det også, så ingen bruker denne som bevis.
 */
export function FolderAccessList({
  rows,
  folderName,
  chainComplete,
  inheritedFrom,
}: {
  rows: FolderAccessPerson[];
  folderName: string;
  /** False = en mappe over denne er skjult for oss, så svaret er ufullstendig. */
  chainComplete: boolean;
  /** Navnene på mappene over denne, nærmeste først. */
  inheritedFrom: string[];
}) {
  const medTilgang = rows.filter((r) => r.can_read);
  const blokkerte = rows.filter((r) => !r.can_read);

  return (
    <div className="space-y-2">
      <Label className="text-xs">Hvem har tilgang</Label>

      {inheritedFrom.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {folderName} ligger under {inheritedFrom.join(" / ")}. Du må ha tilgang
          i hver mappe over også — kravene legges sammen, de erstatter ikke
          hverandre.
        </p>
      )}

      {!chainComplete && (
        <p className="text-xs text-muted-foreground">
          En mappe over denne er ikke synlig for deg, så lista kan være
          ufullstendig.
        </p>
      )}

      {medTilgang.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Ingen har tilgang til denne mappa.
        </p>
      ) : (
        <ul className="divide-y rounded-md border">
          {medTilgang.map((r) => (
            <li key={r.user_id} className="flex flex-wrap items-center gap-2 px-3 py-2">
              <span className="text-sm">{r.full_name}</span>
              <span className="text-xs text-muted-foreground">
                {SYSTEM_ROLE_LABELS[r.role as SystemRole] ?? r.role}
              </span>
              <span className="ml-auto flex flex-wrap items-center gap-1.5">
                <Badge variant="outline">
                  {r.can_write ? "Kan lese og endre" : "Kan lese"}
                </Badge>
                {r.via_rank && <Badge variant="secondary">Via rollen</Badge>}
                {r.grants.map((g) => (
                  <Badge
                    key={`${g.group_id}-${g.folder_id}`}
                    variant="secondary"
                    title={
                      g.inherited
                        ? `Tildelt på ${g.folder_name}, som ligger over denne mappa`
                        : `Tildelt på ${g.folder_name}`
                    }
                  >
                    Via {g.group_name}
                    {g.inherited ? ` (${g.folder_name})` : ""}
                  </Badge>
                ))}
              </span>
            </li>
          ))}
        </ul>
      )}

      {blokkerte.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs text-muted-foreground">
            Disse er med i en gruppe som har en tildeling her, men har likevel
            IKKE tilgang — en mappe over denne stopper dem. Det er med vilje:
            kjeden feiler lukket.
          </p>
          <ul className="divide-y rounded-md border border-dashed">
            {blokkerte.map((r) => (
              <li
                key={r.user_id}
                className="flex flex-wrap items-center gap-2 px-3 py-2"
              >
                <span className="text-sm text-muted-foreground">{r.full_name}</span>
                <span className="ml-auto flex flex-wrap gap-1.5">
                  {r.grants.map((g) => (
                    <Badge key={`${g.group_id}-${g.folder_id}`} variant="outline">
                      {g.group_name} på {g.folder_name}
                    </Badge>
                  ))}
                  <Badge variant="destructive">Blokkert</Badge>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Lista regnes ut etter de samme reglene som databasen håndhever. Avviker
        de, er det databasen som gjelder.
      </p>
    </div>
  );
}
